import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Container, Typography, Paper, Button, IconButton,
  CircularProgress, Divider, Tooltip, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, ToggleButton, ToggleButtonGroup,
  LinearProgress, Chip,
} from '@mui/material';
import ArrowBackIcon     from '@mui/icons-material/ArrowBack';
import UploadIcon        from '@mui/icons-material/Upload';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ImageIcon         from '@mui/icons-material/Image';
import VideocamIcon      from '@mui/icons-material/Videocam';
import {
  collection, addDoc, getDocs, deleteDoc, doc, query, where, serverTimestamp,
} from 'firebase/firestore';
import { ref as storageRef, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../../firebase/config';
import LagbasLogo  from '../../components/LagbasLogo';
import { colors, fontFamily } from '../../theme/tikabTokens';

const accent = colors.orange;

// ── Hämta bakgrunder från Firestore ───────────────────────────────────────────

async function fetchBackgrounds() {
  const q = query(collection(db, 'documentLibrary'), where('category', '==', 'Bakgrundsbild'));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({
    docId: d.id,
    id:    d.data().name,
    label: d.data().name,
    url:   d.data().currentFileUrl || '',
    theme: d.data().note || 'dark',
    type:  d.data().fileType || 'image',
    storagePath: d.data().storagePath || '',
  }));
}

// ── Uppladdningsdialog ────────────────────────────────────────────────────────

function UploadDialog({ open, onClose, onUploaded }) {
  const [file,     setFile]     = useState(null);
  const [name,     setName]     = useState('');
  const [theme,    setTheme]    = useState('dark');
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [error,    setError]    = useState('');
  const inputRef = useRef();

  function handleFileChange(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    // Auto-fill name från filnamn
    if (!name) {
      const base = f.name.replace(/\.[^.]+$/, '').replace(/[^a-zA-Z0-9-]/g, '-').toLowerCase();
      setName('bg-' + base);
    }
    setError('');
  }

  async function handleUpload() {
    if (!file || !name.trim()) {
      setError('Välj en fil och ange ett namn.');
      return;
    }
    setUploading(true);
    setError('');

    try {
      const isVideo = file.type.startsWith('video/');
      const ext     = file.name.split('.').pop();
      const path    = `pitchBackgrounds/${name.trim()}.${ext}`;
      const sRef    = storageRef(storage, path);

      await new Promise((resolve, reject) => {
        const task = uploadBytesResumable(sRef, file);
        task.on('state_changed',
          snap => setProgress(Math.round(snap.bytesTransferred / snap.totalBytes * 100)),
          reject,
          async () => {
            const url = await getDownloadURL(sRef);
            // Spara i documentLibrary
            await addDoc(collection(db, 'documentLibrary'), {
              name:           name.trim(),
              category:       'Bakgrundsbild',
              currentFileUrl: url,
              note:           theme,        // dark | light
              fileType:       isVideo ? 'video' : 'image',
              storagePath:    path,
              createdAt:      serverTimestamp(),
            });
            resolve();
          }
        );
      });

      onUploaded();
      onClose();
    } catch (e) {
      setError('Uppladdning misslyckades: ' + e.message);
    } finally {
      setUploading(false);
      setProgress(0);
    }
  }

  function handleClose() {
    if (uploading) return;
    setFile(null);
    setName('');
    setTheme('dark');
    setProgress(0);
    setError('');
    onClose();
  }

  const isVideo = file?.type?.startsWith('video/');

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>Ladda upp bakgrund</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
        {/* Fil */}
        <input ref={inputRef} type="file" accept="image/*,video/*" style={{ display: 'none' }} onChange={handleFileChange} />
        <Box
          onClick={() => inputRef.current?.click()}
          sx={{
            border: '2px dashed',
            borderColor: file ? accent : 'divider',
            borderRadius: 2, p: 2.5,
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1,
            cursor: 'pointer', transition: 'border-color 0.2s',
            '&:hover': { borderColor: accent },
          }}
        >
          {isVideo
            ? <VideocamIcon sx={{ color: accent, fontSize: 32 }} />
            : <ImageIcon sx={{ color: file ? accent : '#bbb', fontSize: 32 }} />
          }
          <Typography variant="body2" color={file ? 'text.primary' : 'text.secondary'}>
            {file ? file.name : 'Klicka för att välja bild eller video'}
          </Typography>
          {file && (
            <Typography variant="caption" color="text.secondary">
              {(file.size / 1024 / 1024).toFixed(1)} MB — {file.type}
            </Typography>
          )}
        </Box>

        {/* Namn */}
        <TextField
          label="ID / namn"
          value={name}
          onChange={e => setName(e.target.value)}
          size="small"
          helperText="Används som identifierare, t.ex. bg-dark-gear"
          inputProps={{ style: { fontFamily: 'monospace' } }}
        />

        {/* Tema */}
        <Box>
          <Typography variant="caption" color="text.secondary" display="block" mb={0.75}>
            Bildtema (påverkar textfärg)
          </Typography>
          <ToggleButtonGroup value={theme} exclusive onChange={(_, v) => v && setTheme(v)} size="small">
            <ToggleButton value="dark" sx={{ px: 2, textTransform: 'none' }}>Mörk</ToggleButton>
            <ToggleButton value="light" sx={{ px: 2, textTransform: 'none' }}>Ljus</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Progress */}
        {uploading && (
          <Box>
            <LinearProgress variant="determinate" value={progress} sx={{ borderRadius: 1 }} />
            <Typography variant="caption" color="text.secondary" mt={0.5} display="block" textAlign="right">
              {progress}%
            </Typography>
          </Box>
        )}

        {error && <Typography color="error" variant="caption">{error}</Typography>}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={uploading}>Avbryt</Button>
        <Button
          variant="contained"
          onClick={handleUpload}
          disabled={uploading || !file || !name.trim()}
          sx={{ bgcolor: accent, '&:hover': { bgcolor: '#c44d00' }, textTransform: 'none' }}
          startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : <UploadIcon />}
        >
          {uploading ? `Laddar upp… ${progress}%` : 'Ladda upp'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Bakgrundskort ─────────────────────────────────────────────────────────────

function BgCard({ bg, onDelete }) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const isVideo = bg.type === 'video';

  return (
    <Paper sx={{ borderRadius: 2, overflow: 'hidden', position: 'relative', '&:hover .overlay': { opacity: 1 } }}>
      {/* Preview */}
      <Box sx={{ aspectRatio: '16/9', bgcolor: '#111', position: 'relative', overflow: 'hidden' }}>
        {isVideo ? (
          <Box
            component="video"
            src={bg.url}
            autoPlay
            muted
            loop
            playsInline
            sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        ) : bg.url ? (
          <Box sx={{
            position: 'absolute', inset: 0,
            backgroundImage: `url("${bg.url}")`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }} />
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <ImageIcon sx={{ color: '#444', fontSize: 32 }} />
          </Box>
        )}
        {/* Hover-overlay med åtgärder */}
        <Box className="overlay" sx={{
          position: 'absolute', inset: 0,
          bgcolor: 'rgba(0,0,0,0.55)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          opacity: 0, transition: 'opacity 0.2s',
        }}>
          <Tooltip title="Radera">
            <IconButton
              sx={{ color: '#fff', bgcolor: 'rgba(211,47,47,0.7)', '&:hover': { bgcolor: '#d32f2f' } }}
              onClick={() => setConfirmDelete(true)}
            >
              <DeleteOutlineIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Info */}
      <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
        {isVideo
          ? <VideocamIcon sx={{ fontSize: 16, color: '#aaa', flexShrink: 0 }} />
          : <ImageIcon sx={{ fontSize: 16, color: '#aaa', flexShrink: 0 }} />
        }
        <Typography variant="body2" fontWeight={600} noWrap sx={{ flex: 1, fontFamily: 'monospace', fontSize: '0.78rem' }}>
          {bg.id}
        </Typography>
        <Chip
          label={bg.theme}
          size="small"
          sx={{
            bgcolor: bg.theme === 'dark' ? '#222' : '#f5f5f5',
            color: bg.theme === 'dark' ? '#ddd' : '#333',
            fontSize: '0.65rem', height: 18,
          }}
        />
      </Box>

      {/* Radera-dialog */}
      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Radera bakgrundsbild?</DialogTitle>
        <DialogContent>
          <Typography>
            <strong>{bg.id}</strong> raderas permanent från bildbiblioteket. Presentationer som använder den kommer att sakna bakgrund.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)}>Avbryt</Button>
          <Button
            variant="contained" color="error"
            onClick={() => { setConfirmDelete(false); onDelete(bg); }}
          >
            Radera
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

// ── Huvud ─────────────────────────────────────────────────────────────────────

export default function PitchBackgrounds() {
  const navigate             = useNavigate();
  const [backgrounds, setBackgrounds] = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [uploadOpen,  setUploadOpen]  = useState(false);

  async function reload() {
    setLoading(true);
    const bgs = await fetchBackgrounds();
    setBackgrounds(bgs);
    setLoading(false);
  }

  useEffect(() => { reload(); }, []);

  async function handleDelete(bg) {
    // Ta bort från Storage om vi har sökvägen
    if (bg.storagePath) {
      try { await deleteObject(storageRef(storage, bg.storagePath)); } catch (_) {}
    }
    // Ta bort Firestore-dokument
    await deleteDoc(doc(db, 'documentLibrary', bg.docId));
    setBackgrounds(prev => prev.filter(b => b.docId !== bg.docId));
  }

  const images = backgrounds.filter(b => b.type !== 'video');
  const videos = backgrounds.filter(b => b.type === 'video');

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <Box sx={{
        bgcolor: '#1a1a1a', color: 'white', py: 1.5, px: 3,
        display: 'flex', alignItems: 'center', gap: 2,
      }}>
        <LagbasLogo />
        <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)', mx: -0.5 }} />
        <IconButton color="inherit" size="small" onClick={() => navigate(-1)}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" fontWeight="bold">
            Pitch<span style={{ color: accent }}>N</span> — Bakgrundsbibliotek
          </Typography>
          <Typography variant="caption" sx={{ color: '#aaa' }}>
            Bilder och filmer som används som bakgrund i presentationer
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<UploadIcon />}
          onClick={() => setUploadOpen(true)}
          sx={{ bgcolor: accent, '&:hover': { bgcolor: '#c44d00' }, fontFamily }}
        >
          Ladda upp
        </Button>
      </Box>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Bilder */}
            <Box sx={{ mb: 1 }}>
              <Typography variant="h6" fontWeight={700} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <ImageIcon sx={{ color: accent }} /> Bakgrundsbilder
                <Chip label={images.length} size="small" sx={{ ml: 1 }} />
              </Typography>
              {images.length === 0 ? (
                <Typography color="text.secondary" sx={{ mb: 4 }}>Inga bilder ännu — ladda upp för att komma igång.</Typography>
              ) : (
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 2, mb: 4 }}>
                  {images.map(bg => (
                    <BgCard key={bg.docId} bg={bg} onDelete={handleDelete} />
                  ))}
                </Box>
              )}
            </Box>

            {/* Videos */}
            {videos.length > 0 && (
              <Box>
                <Typography variant="h6" fontWeight={700} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <VideocamIcon sx={{ color: accent }} /> Bakgrundsfilmer
                  <Chip label={videos.length} size="small" sx={{ ml: 1 }} />
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 2 }}>
                  {videos.map(bg => (
                    <BgCard key={bg.docId} bg={bg} onDelete={handleDelete} />
                  ))}
                </Box>
              </Box>
            )}

            {backgrounds.length === 0 && (
              <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 2 }}>
                <ImageIcon sx={{ fontSize: 48, color: '#ccc', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Inga bakgrundsbilder ännu
                </Typography>
                <Typography color="text.secondary" sx={{ mb: 3 }}>
                  Ladda upp bilder eller filmer att använda som bakgrund i dina presentationer
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<UploadIcon />}
                  onClick={() => setUploadOpen(true)}
                  sx={{ bgcolor: accent, '&:hover': { bgcolor: '#c44d00' } }}
                >
                  Ladda upp bakgrund
                </Button>
              </Paper>
            )}
          </>
        )}
      </Container>

      <UploadDialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onUploaded={reload}
      />
    </Box>
  );
}
